import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Edit, FileText, AlertCircle, ArrowRight, User, Clock, MapPin } from 'lucide-react';
import { colors, detailStyles } from './detail.styles';
/**
 * Configuration for how specific fields are displayed
 */
const fieldConfig = {
    description: { label: 'Description', icon: _jsx(FileText, { size: 14 }) },
    ro_number: { label: 'RO Number', icon: _jsx(FileText, { size: 14 }) },
    hat_number: { label: 'HAT Number', icon: _jsx(FileText, { size: 14 }) },
    po_number: { label: 'PO Number', icon: _jsx(FileText, { size: 14 }) },
    priority: {
        label: 'Priority',
        icon: _jsx(AlertCircle, { size: 14 }),
        format: (v) => v
            ? String(v)
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (l) => l.toUpperCase())
            : '-',
    },
    state: {
        label: 'State',
        icon: _jsx(ArrowRight, { size: 14 }),
        format: (v) => v
            ? String(v)
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (l) => l.toUpperCase())
            : '-',
    },
    assigned_user_id: { label: 'Assigned User', icon: _jsx(User, { size: 14 }) },
    vehicle_id: { label: 'Vehicle', icon: _jsx(FileText, { size: 14 }) },
    active_department_id: { label: 'Active Department', icon: _jsx(MapPin, { size: 14 }) },
    department_entry_time: {
        label: 'Department Entry',
        icon: _jsx(Clock, { size: 14 }),
        format: (v) => {
            if (!v)
                return '-';
            const date = new Date(v);
            return date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
            });
        },
    },
};
/**
 * Check if a value is an {old, new} change object
 */
function isOldNewObject(value) {
    return typeof value === 'object' && value !== null && 'old' in value && 'new' in value;
}
/**
 * Format a value for display, applying field-specific formatters
 */
function formatValue(field, value) {
    if (value === null || value === undefined)
        return '-';
    if (value === '')
        return '(empty)';
    const config = fieldConfig[field];
    if (config?.format) {
        return config.format(value);
    }
    // Handle UUIDs (show truncated)
    if (typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
        return value.substring(0, 8) + '...';
    }
    // Handle objects (but not old/new objects which are handled separately)
    if (typeof value === 'object' && !isOldNewObject(value)) {
        return JSON.stringify(value);
    }
    return String(value);
}
/**
 * Get display label for a field
 */
function getFieldLabel(field) {
    const config = fieldConfig[field];
    if (config?.label)
        return config.label;
    return field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}
/**
 * Get icon for a field
 */
function getFieldIcon(field) {
    const config = fieldConfig[field];
    return config?.icon || _jsx(Edit, { size: 14 });
}
export const WorkOrderUpdateDetail = ({ metadata }) => {
    const changes = metadata.changes;
    if (!changes || Object.keys(changes).length === 0) {
        return (_jsx("div", { style: detailStyles.detailContent, children: _jsxs("div", { style: detailStyles.emptyState, children: [_jsx(AlertCircle, { size: 20, style: { color: colors.text.tertiary } }), _jsx("span", { children: "No change details available" })] }) }));
    }
    return (_jsxs("div", { style: detailStyles.detailContent, children: [_jsxs("div", { style: detailStyles.sectionHeader, children: [_jsx(Edit, { size: 16, style: { color: colors.accent.amber } }), _jsx("span", { children: "Fields Updated" })] }), _jsx("div", { style: detailStyles.changeCards, children: Object.entries(changes).map(([field, value]) => {
                    const hasOldNew = isOldNewObject(value);
                    const oldVal = hasOldNew ? value.old : null;
                    const newVal = hasOldNew ? value.new : value;
                    return (_jsxs("div", { style: detailStyles.changeCard, children: [_jsxs("div", { style: detailStyles.changeCardHeader, children: [_jsx("span", { style: { color: colors.accent.amber }, children: getFieldIcon(field) }), _jsx("span", { style: detailStyles.changeCardLabel, children: getFieldLabel(field) })] }), hasOldNew ? (_jsxs("div", { style: detailStyles.changeCardOldNew, children: [_jsxs("div", { style: detailStyles.changeCardOldValue, children: [_jsx("span", { style: detailStyles.changeCardOldLabel, children: "From:" }), _jsx("span", { style: detailStyles.changeCardOldText, children: formatValue(field, oldVal) })] }), _jsx(ArrowRight, { size: 14, style: { color: colors.accent.blue, flexShrink: 0 } }), _jsxs("div", { style: detailStyles.changeCardNewValue, children: [_jsx("span", { style: detailStyles.changeCardNewLabel, children: "To:" }), _jsx("span", { style: detailStyles.changeCardNewText, children: formatValue(field, newVal) })] })] })) : (_jsx("div", { style: detailStyles.changeCardValue, children: formatValue(field, value) }))] }, field));
                }) }), _jsx("div", { style: detailStyles.changesSummary, children: _jsxs("span", { style: detailStyles.changesSummaryText, children: [Object.keys(changes).length, " field", Object.keys(changes).length !== 1 ? 's' : '', " updated"] }) })] }));
};
export default WorkOrderUpdateDetail;
